import { Type } from "@sinclair/typebox";
import { FileRefSchema } from "./files.js";
import { ActionGrantSchema, ActionRequestSchema, BudgetSchema } from "./action.js";
import { IdSchema, JsonValueSchema, NonNegativeIntegerSchema, Sha256Schema, TimestampSchema, } from "./shared.js";
import { ModelActionRejectionEvidenceFields, ModelDecisionEvidenceFields, WorkingPlanSchema, } from "./task.js";
export const RuntimeProfileId = "agentic_bioinformatics";
export const GuideFunctionDigestsSchema = Type.Record(Type.String({ pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$" }), Sha256Schema, { maxProperties: 4096 });
const SlugSchema = Type.String({
    minLength: 1,
    maxLength: 128,
    pattern: "^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$",
});
const TextSchema = Type.String({ minLength: 1, maxLength: 2_000 });
export const SuggestedToolSchema = Type.Object({
    server: IdSchema,
    tool: IdSchema,
    function: IdSchema,
    purpose: TextSchema,
}, { additionalProperties: false });
export const GuideApplicabilitySchema = Type.Object({
    intentSummary: TextSchema,
    positiveExamples: Type.Array(TextSchema, { minItems: 1, maxItems: 32 }),
    negativeExamples: Type.Array(TextSchema, { maxItems: 32 }),
    keywords: Type.Array(TextSchema, { minItems: 1, maxItems: 64 }),
    excludedKeywords: Type.Array(TextSchema, { maxItems: 64 }),
    minimumScore: Type.Integer({ minimum: 1, maximum: 100 }),
    priority: Type.Integer({ minimum: -1_000, maximum: 1_000 }),
}, { additionalProperties: false });
export const SuggestedStepSchema = Type.Object({
    stepId: SlugSchema,
    title: TextSchema,
    purpose: TextSchema,
    suggestedTools: Type.Array(SuggestedToolSchema, { maxItems: 16 }),
    alternatives: Type.Array(TextSchema, { maxItems: 16 }),
    evidenceToCapture: Type.Array(TextSchema, { maxItems: 16 }),
}, { additionalProperties: false });
export const GuideMetricSchema = Type.Object({ metricId: SlugSchema, description: TextSchema, interpretation: TextSchema }, { additionalProperties: false });
export const HumanCheckpointSchema = Type.Object({
    checkpointId: SlugSchema,
    when: TextSchema,
    question: TextSchema,
    requiredByDefault: Type.Boolean(),
}, { additionalProperties: false });
export const GuideGuidanceSchema = Type.Object({
    planningPrinciples: Type.Array(TextSchema, { minItems: 1, maxItems: 32 }),
    suggestedSteps: Type.Array(SuggestedStepSchema, { minItems: 1, maxItems: 64 }),
    metrics: Type.Array(GuideMetricSchema, { maxItems: 32 }),
    successCriteria: Type.Array(TextSchema, { minItems: 1, maxItems: 32 }),
    humanCheckpoints: Type.Array(HumanCheckpointSchema, { maxItems: 32 }),
    limitations: Type.Array(TextSchema, { minItems: 1, maxItems: 32 }),
}, { additionalProperties: false });
const CanaryAssertionKindSchema = Type.Union([
    Type.Literal("input_equals_case_input"),
    Type.Literal("input_equals_prior_output"),
    Type.Literal("input_files_bound_to_prior_outputs"),
    Type.Literal("output_equals_case_input"),
    Type.Literal("output_length_equals_case_input"),
    Type.Literal("output_finite_number"),
    Type.Literal("output_non_empty"),
    Type.Literal("output_balanced_dot_bracket"),
    Type.Literal("output_ensemble_free_energy_not_above_mfe"),
    Type.Literal("output_accessibility_consistent"),
    Type.Literal("output_mmcif_covalent_geometry"),
    Type.Literal("output_evidence_verdicts_consistent"),
    Type.Literal("final_computational_disclaimer"),
    Type.Literal("final_contains_case_input"),
]);
export const CanaryEvidenceRuleSchema = Type.Object({
    rowsPath: IdSchema,
    verdictsPath: IdSchema,
    verdictValuePath: IdSchema,
    identityPaths: Type.Array(IdSchema, { minItems: 1, maxItems: 16, uniqueItems: true }),
    scopePaths: Type.Array(IdSchema, { minItems: 1, maxItems: 8, uniqueItems: true }),
    groupPaths: Type.Array(IdSchema, { minItems: 1, maxItems: 8, uniqueItems: true }),
    minimumGroups: Type.Integer({ minimum: 1, maximum: 1024 }),
    predicates: Type.Array(Type.Object({
        path: IdSchema,
        comparison: Type.Union([
            Type.Literal("equals"),
            Type.Literal("gte"),
            Type.Literal("lte"),
        ]),
        expected: Type.Union([Type.String({ maxLength: 1024 }), Type.Number(), Type.Boolean()]),
    }, { additionalProperties: false }), { minItems: 1, maxItems: 16 }),
}, { additionalProperties: false });
export const CanaryFileLineageRuleSchema = Type.Object({
    filePath: IdSchema,
    producerFilePath: IdSchema,
    producerLabelPath: IdSchema,
    producerLabelMode: Type.Optional(Type.Union([Type.Literal("value"), Type.Literal("path_parent")])),
    argumentBindings: Type.Optional(Type.Array(Type.Object({
        inputScope: Type.Union([Type.Literal("member"), Type.Literal("arguments")]),
        inputPath: IdSchema,
        producerPath: IdSchema,
    }, { additionalProperties: false }), { minItems: 1, maxItems: 16 })),
    labels: Type.Array(Type.Object({
        inputPath: IdSchema,
        equals: Type.String({ minLength: 1, maxLength: 128 }),
        template: Type.String({ minLength: 1, maxLength: 512 }),
        fileNameSuffix: Type.Optional(Type.String({ minLength: 1, maxLength: 128, pattern: "^[A-Za-z0-9_.-]+$" })),
    }, { additionalProperties: false }), { minItems: 1, maxItems: 16 }),
}, { additionalProperties: false });
export const CanaryAssertionSchema = Type.Object({
    assertionId: SlugSchema,
    quantifier: Type.Optional(Type.Union([Type.Literal("any"), Type.Literal("all")])),
    where: Type.Optional(Type.Array(Type.Object({
        source: Type.Union([Type.Literal("arguments"), Type.Literal("result")]),
        path: IdSchema,
        equals: Type.Union([Type.String({ maxLength: 1024 }), Type.Number(), Type.Boolean()]),
    }, { additionalProperties: false }), { minItems: 1, maxItems: 16 })),
    kind: CanaryAssertionKindSchema,
    evidenceRule: Type.Optional(CanaryEvidenceRuleSchema),
    lineageRule: Type.Optional(CanaryFileLineageRuleSchema),
    tool: Type.Optional(IdSchema),
    function: Type.Optional(IdSchema),
    path: Type.Optional(IdSchema),
    caseInput: Type.Optional(IdSchema),
    otherPath: Type.Optional(IdSchema),
    priorTool: Type.Optional(IdSchema),
    priorFunction: Type.Optional(IdSchema),
    priorPath: Type.Optional(IdSchema),
}, { additionalProperties: false });
export const CanaryExpectedEvidenceSchema = Type.Object({
    requiredToolCalls: Type.Array(SuggestedToolSchema, { minItems: 1, maxItems: 16 }),
    requiredOutputFields: Type.Record(IdSchema, Type.Array(IdSchema, { maxItems: 64 })),
    assertions: Type.Array(CanaryAssertionSchema, { minItems: 1, maxItems: 32 }),
}, { additionalProperties: false });
export const CanaryBudgetSchema = Type.Object({
    maxActions: Type.Integer({ minimum: 1, maximum: 100 }),
    maxToolCalls: Type.Integer({ minimum: 1, maximum: 100 }),
    maxWallTimeSeconds: Type.Integer({ minimum: 1, maximum: 21_600 }),
    maxLlmTokens: Type.Integer({ minimum: 1, maximum: 12_000_000 }),
}, { additionalProperties: false });
export const CanaryFixtureSchema = Type.Object({
    fixtureId: SlugSchema,
    file: Type.String({ pattern: "^inputs/[A-Za-z0-9][A-Za-z0-9_.-]{0,127}$", maxLength: 135 }),
    filename: Type.String({ pattern: "^[A-Za-z0-9][A-Za-z0-9_.-]{0,127}$", maxLength: 128 }),
    sha256: Sha256Schema,
    sizeBytes: Type.Integer({ minimum: 1, maximum: 1024 * 1024 }),
}, { additionalProperties: false });
export const CanaryFixtureBindingSchema = Type.Object({
    fixtureId: SlugSchema,
    sha256: Sha256Schema,
    sizeBytes: Type.Integer({ minimum: 1, maximum: 1024 * 1024 }),
    fileRef: FileRefSchema,
    workspacePath: Type.String({ minLength: 1, maxLength: 4096 }),
}, { additionalProperties: false });
const CanaryFixtureBindingsSchema = Type.Optional(Type.Array(CanaryFixtureBindingSchema, { minItems: 1, maxItems: 16 }));
export const CanaryCaseSchema = Type.Object({
    schemaVersion: Type.Literal("1.0.0"),
    caseId: SlugSchema,
    fixtures: Type.Optional(Type.Array(CanaryFixtureSchema, { minItems: 1, maxItems: 16 })),
    goal: TextSchema,
    inputs: JsonValueSchema,
    expectedEvidence: CanaryExpectedEvidenceSchema,
    budget: CanaryBudgetSchema,
    containsPrivateData: Type.Literal(false),
    requiresRealModel: Type.Literal(true),
    requiresRealMcp: Type.Literal(true),
}, { additionalProperties: false });
export const GuideDefinitionContentSchema = Type.Object({
    schemaVersion: Type.Literal("1.0.0"),
    definitionId: SlugSchema,
    displayName: Type.String({ minLength: 1, maxLength: 300 }),
    description: TextSchema,
    applicability: GuideApplicabilitySchema,
    guidance: GuideGuidanceSchema,
    canaryFile: Type.String({
        pattern: "^eval_cases/[A-Za-z0-9_.-]+\\.ya?ml$",
        maxLength: 255,
    }),
    canary: CanaryCaseSchema,
}, { additionalProperties: false });
export const GuideDefinitionSchema = Type.Composite([
    GuideDefinitionContentSchema,
    Type.Object({
        contentDigest: Sha256Schema,
        status: Type.Union([
            Type.Literal("draft"),
            Type.Literal("blocked"),
            Type.Literal("validated"),
            Type.Literal("approved"),
            Type.Literal("deprecated"),
        ]),
    }, { additionalProperties: false }),
], { additionalProperties: false });
export const GuideApprovalSchema = Type.Object({
    definitionId: SlugSchema,
    contentDigest: Sha256Schema,
    traceDigest: Sha256Schema,
    runtimeDigest: Sha256Schema,
    protocolDigest: Sha256Schema,
    serverId: IdSchema,
    functionDigests: Type.Optional(GuideFunctionDigestsSchema),
    approvedBy: IdSchema,
    approvedAt: TimestampSchema,
}, { additionalProperties: false });
export const GuideCanaryEvidenceSchema = Type.Object({
    schemaVersion: Type.Literal("1.0.0"),
    definitionId: SlugSchema,
    contentDigest: Sha256Schema,
    traceDigest: Sha256Schema,
    runtimeDigest: Sha256Schema,
    protocolDigest: Sha256Schema,
    serverId: IdSchema,
    functionDigests: Type.Optional(GuideFunctionDigestsSchema),
    executionKind: Type.Literal("real"),
    runtimeProfileId: Type.Literal(RuntimeProfileId),
    modelEvidence: Type.Literal("real"),
    mcpEvidence: Type.Literal("real"),
    authorizationEnvelopeDigest: Sha256Schema,
    validatorDigest: Sha256Schema,
    cleanupEvidenceDigest: Sha256Schema,
    passed: Type.Boolean(),
    executedAt: TimestampSchema,
    cleanupCompletedAt: TimestampSchema,
}, { additionalProperties: false });
export const GuideCanaryAttestationSchema = Type.Object({
    keyId: IdSchema,
    algorithm: Type.Literal("Ed25519"),
    evidence: GuideCanaryEvidenceSchema,
    signature: Type.String({
        minLength: 86,
        maxLength: 86,
        pattern: "^[A-Za-z0-9_-]+$",
    }),
}, { additionalProperties: false });
const GuideCanaryModelDecisionTraceSchema = Type.Object({
    ...ModelDecisionEvidenceFields,
    occurredAt: TimestampSchema,
}, { additionalProperties: false });
const GuideCanaryActionRejectionTraceSchema = Type.Object({
    ...ModelActionRejectionEvidenceFields,
    occurredAt: TimestampSchema,
}, { additionalProperties: false });
const GuideCanaryObservationTraceSchema = Type.Object({
    observationId: IdSchema,
    actionId: IdSchema,
    grantId: IdSchema,
    status: Type.Union([
        Type.Literal("succeeded"),
        Type.Literal("failed"),
        Type.Literal("cancelled"),
        Type.Literal("timed_out"),
    ]),
    resultDigest: Sha256Schema,
    result: JsonValueSchema,
    actualCost: BudgetSchema,
}, { additionalProperties: false });
const GuideCanaryActionTraceSchema = Type.Object({
    tool: IdSchema,
    function: IdSchema,
    request: ActionRequestSchema,
    grant: ActionGrantSchema,
    observation: GuideCanaryObservationTraceSchema,
}, { additionalProperties: false });
export const GuideCanaryTraceSchema = Type.Object({
    schemaVersion: Type.Literal("1.0.0"),
    fixtureBindings: CanaryFixtureBindingsSchema,
    functionDigests: Type.Optional(GuideFunctionDigestsSchema),
    definitionId: SlugSchema,
    contentDigest: Sha256Schema,
    runtimeDigest: Sha256Schema,
    protocolDigest: Sha256Schema,
    serverId: IdSchema,
    executionKind: Type.Literal("real"),
    runtimeProfileId: Type.Literal(RuntimeProfileId),
    modelEvidence: Type.Literal("real"),
    mcpEvidence: Type.Literal("real"),
    sessionId: IdSchema,
    turnId: IdSchema,
    runnerId: Type.Optional(IdSchema),
    plan: WorkingPlanSchema,
    authorizationEnvelopeId: IdSchema,
    authorizationEnvelopeDigest: Sha256Schema,
    modelDecisions: Type.Array(GuideCanaryModelDecisionTraceSchema, {
        minItems: 1,
        maxItems: 1_024,
    }),
    actionRejections: Type.Array(GuideCanaryActionRejectionTraceSchema, { maxItems: 256 }),
    actions: Type.Array(GuideCanaryActionTraceSchema, { maxItems: 100 }),
    final: Type.Object({
        outcome: Type.Union([
            Type.Literal("completed"),
            Type.Literal("reply"),
            Type.Literal("paused"),
            Type.Literal("failed"),
        ]),
        response: JsonValueSchema,
    }, { additionalProperties: false }),
    observedBudget: Type.Object({
        actions: NonNegativeIntegerSchema,
        toolCalls: NonNegativeIntegerSchema,
        activeWallTimeMilliseconds: NonNegativeIntegerSchema,
        llmTokens: NonNegativeIntegerSchema,
        actualCost: BudgetSchema,
    }, { additionalProperties: false }),
    eventLogDigest: Sha256Schema,
    failures: Type.Array(Type.Object({
        code: IdSchema,
        stage: IdSchema,
        message: Type.String({ minLength: 1, maxLength: 2_000 }),
    }, { additionalProperties: false }), { maxItems: 128 }),
    startedAt: TimestampSchema,
    completedAt: TimestampSchema,
    cleanup: Type.Object({
        jobId: IdSchema,
        stage: Type.Literal("complete"),
        attempts: NonNegativeIntegerSchema,
        completedAt: TimestampSchema,
    }, { additionalProperties: false }),
}, { additionalProperties: false });
export const GuideCanaryValidationReportSchema = Type.Object({
    schemaVersion: Type.Literal("1.0.0"),
    definitionId: SlugSchema,
    contentDigest: Sha256Schema,
    traceDigest: Sha256Schema,
    assertions: Type.Array(Type.Object({
        assertionId: SlugSchema,
        passed: Type.Boolean(),
        code: IdSchema,
    }, { additionalProperties: false }), { minItems: 1, maxItems: 32 }),
    budgetPassed: Type.Boolean(),
    problems: Type.Array(Type.String({ minLength: 1, maxLength: 2_000 }), { maxItems: 256 }),
    passed: Type.Boolean(),
}, { additionalProperties: false });
export const GuideCanaryPreflightSchema = Type.Object({
    schemaVersion: Type.Literal("1.0.0"),
    kind: Type.Literal("guide-canary-preflight"),
    fixtureBindings: CanaryFixtureBindingsSchema,
    definitionId: SlugSchema,
    contentDigest: Sha256Schema,
    canaryDigest: Sha256Schema,
    goalDigest: Sha256Schema,
    runtimeSnapshot: Type.Object({
        runtimeDigest: Sha256Schema,
        protocolDigest: Sha256Schema,
        serverId: IdSchema,
        functionDigests: Type.Optional(GuideFunctionDigestsSchema),
    }, { additionalProperties: false }),
    sessionId: IdSchema,
    targetDigest: Sha256Schema,
    turnId: IdSchema,
    plan: WorkingPlanSchema,
    planSnapshotDigest: Sha256Schema,
    authorizationEnvelopeId: IdSchema,
    authorizationEnvelopeDigest: Sha256Schema,
    preflightEventLogDigest: Sha256Schema,
    modelPhaseMilliseconds: NonNegativeIntegerSchema,
    budget: CanaryBudgetSchema,
    requiredToolCalls: Type.Array(SuggestedToolSchema, { minItems: 1, maxItems: 16 }),
    containsPrivateData: Type.Literal(false),
    createdAt: TimestampSchema,
    expiresAt: TimestampSchema,
}, { additionalProperties: false });
export const GuidePlanningSelectionSchema = Type.Object({
    definitionId: SlugSchema,
    title: Type.String({ minLength: 1, maxLength: 300 }),
    contentDigest: Sha256Schema,
    matchReason: Type.String({ minLength: 1, maxLength: 1_000 }),
    planningGuidance: Type.Array(TextSchema, { maxItems: 256 }),
    limitations: Type.Array(TextSchema, { maxItems: 32 }),
    canaryInputs: Type.Optional(JsonValueSchema),
    canaryExecutionLimits: Type.Optional(Type.Object({
        maxActions: Type.Integer({ minimum: 1, maximum: 100 }),
        maxToolCalls: Type.Integer({ minimum: 1, maximum: 100 }),
    }, { additionalProperties: false })),
}, { additionalProperties: false });
export const GuideMessageBodySchema = Type.Union([
    Type.Object({ kind: Type.Literal("definition"), value: GuideDefinitionSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("canary"), value: GuideCanaryAttestationSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("approval"), value: GuideApprovalSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("selection"), value: GuidePlanningSelectionSchema }, { additionalProperties: false }),
]);
//# sourceMappingURL=guide.js.map